/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import isEmpty from 'lodash.isempty';
import {globalSearchSelector} from '@ion/applications/src/globalsearch/selector';
import useCurrentGlobalGroupId from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import useFilePermissionsAction from '@ion/applications/src/hooks/useFilePermissionsAction';
import useJobSearchRerun from '@ion/applications/src/hooks/useJobSearchRerun';
import useAuthenticatedUser from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import {showPendingJobs} from '@ion/applications/src/pages/devices/state';
import {adminDevicesViewIsEditingSelector} from '@ion/applications/src/pages/devices/state-selectors';
import {ExportContext} from '@ion/applications/src/types/redux';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {CRUDPermissions, DownloadFileTypes, Entity, GenericObject} from '@ion/core/src/types/entities';
import CSVExportButton from '@ion/dls/src/components/buttons/CSVExportButton';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import RefreshGridButton from '@ion/dls/src/components/buttons/RefreshGridButton';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import IonDialog from '@ion/dls/src/components/dialogs/IonDialog';
import SnackbarMessage from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import IonTooltip from '@ion/dls/src/components/tooltip/IonTooltip';
import EmailExportRequestDialog from '@ion/dls/src/export/EmailExportRequestDialog';
import CancelIcon from '@ion/dls/src/icons/CancelIcon';
import {RetryIcon} from '@ion/dls/src/icons/RetryIcon';
import Render from '@ion/dls/src/render/Render';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import useCancelJob from '@ion/query/src/hooks/useCancelJob';
import Features from '@/security/Features';
import {cancelJobs, showDownloadJobs} from '@/store/actions/downloadjobs';
import {displayRerunJobSearchEngineDialog} from '@/store/actions/jobsearchengine';
import {jobCancelSelectedSelector, jobCancelShowDialogSelector} from '@/store/selectors/jobsearchengine';
import DownloadJobCancelPrompt from '../jobs/DownloadJobCancelPrompt';
import DownloadJobRerunPrompt from '../jobs/DownloadJobRerunPrompt';
import {isJobCancelDisabled, isRerunFailedAllowed} from './JobSearchEngineRowEditor';
import {getTasksDevicesCounts, handleDisable} from './JobSearchEngineUtils';
import {getDeleteMessage} from './JobSearchUtils';
import JobTab from './JobTab';
import useJobSearchEngineExport from './useJobSearchEngineExport';
import useJobSearchEngineRerun from './useJobSearchEngineRerun';

export interface ViewJobProps {
  open: boolean;
  setShowJob: Function;
  selectedJobId: number;
  enableEmailExport?: boolean;
  setSelectedJobId: Function;
  styleOverrides?: (theme: DLSTheme) => GenericObject;
  jobsPermissions?: CRUDPermissions;
  isTaskDeviceClicked?: boolean;
  setIsTaskDeviceClicked?: Function;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    icons : {
      display        : 'flex',
      justifyContent : 'end'
    }
  };
});

export const ViewJob = ({
  open,
  setShowJob,
  setSelectedJobId,
  selectedJobId,
  jobsPermissions,
  setIsTaskDeviceClicked,
  isTaskDeviceClicked = false,
  enableEmailExport = true
}: ViewJobProps) => {
  const jobTabRef                       = useRef(null);
  const classes                         = useStyles();
  const dispatch                        = useDispatch();
  const {
    search : {
      isViewing
    }
  } = useSelector(globalSearchSelector);
  const {account}                       = useAuthenticatedUser();
  const globalGroupaccountId            = useCurrentGlobalGroupId();
  const accountId                       = isViewing ? entityId(account) : globalGroupaccountId;
  const cancelShow                      = useSelector(jobCancelShowDialogSelector);
  const cancelSelected                  = useSelector(jobCancelSelectedSelector);
  const showViewDevice                  = useSelector(adminDevicesViewIsEditingSelector);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentJob, setCurrentJob]     = useState();
  const [taskId, setTaskId]             = useState();
  const [isLoading, setIsLoading]       = useState(true);

  const [isRerunFailed, setIsRerunFailed] = useState(false);
  
  const getExportColumnDefinitions = function() {
    return {
      columns          : {},
      individualColumn : translate('jobsearchengine:exportColumns', {returnObjects: true})
    };
  };

  const {
    failureMessage,
    successMessage
  } = translate('jobsearchengine:cancel', {
    returnObjects : true,
    entity        : Entity.job
  }) || {};

  const {
    isError: jobCancelError,
    isSuccess: jobCancelSuccess,
    isLoading: jobCancelLoading,
    cancelJob: cancelSelectedJob
  } = useCancelJob();
  
  const {
    rerunShow,
    rerunSelected,
    rerunFailedShow,
    rerunFailedTaskShow
  } = useJobSearchEngineRerun();
  
  const {
    reRunJobSelected,
    jobRerunLoading,
    reRunJobError,
    rerunFailed,
    rerunJob
  } = useJobSearchRerun();

  const {
    isEmailExportRequest,
    setIsEmailExportRequest,
    onCloseEmailExportDialog
  } = useJobSearchEngineExport();

  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: onComplete,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  const params = {
    sortOn           : [{propertyName: 'jobId', order: 'DESC'}],
    query            : `(jobId:${selectedJobId})`,
    includeSubGroups : true,
    accountId
  };
  
  const onRefreshPage = () => {
    jobTabRef?.current?.refresh();
  };

  const onExport = useCallback(function() {
    setIsEmailExportRequest?.(true);
  }, [setIsEmailExportRequest]);

  const onJobDeleteConfirm = useCallback(function() {
    cancelSelectedJob(cancelSelected);
  }, [cancelSelected]);
  
  const onJobDeleteCancel = useCallback(function() {
    dispatch(cancelJobs(cancelSelected, false));
  }, [cancelSelected]);
  
  const getJobDeleteMessage = useCallback(function() {
    return getDeleteMessage(cancelSelected);
  }, [cancelSelected]);

  const onCloseDialog = useCallback(() => {
    setShowJob?.(false);
    setSelectedJobId?.(null);
    if (showPendingJobs) {
      dispatch(showPendingJobs(null, false));
    }
    dispatch(showDownloadJobs(null, false));
  }, [dispatch]);

  const onRerunConfirm = useCallback(function() {
    dispatch(displayRerunJobSearchEngineDialog(rerunSelected, false, false));
    if (rerunFailedShow || rerunFailedTaskShow) {
      rerunFailed(rerunSelected, taskId, rerunFailedTaskShow);
    } else {
      rerunJob(rerunSelected);
    }
  }, [taskId, rerunSelected, rerunFailedShow, rerunFailedTaskShow]);

  const [hasFilePermissionFeature, setHasFilePermissionFeature] = useState(false);
  const hasAssignFilePermission                                 = useFilePermissionsAction({
    Features,
    isEditor : true
  });
    
  useEffect(() => {
    const handleAccess = () => {
      if (currentJob) {
        if (!isEmpty(hasAssignFilePermission)) {
          setHasFilePermissionFeature(handleDisable(currentJob, Features, hasAssignFilePermission));
        }
      }
    };
      
    handleAccess();
  }, [
    hasAssignFilePermission,
    Features,
    currentJob
  ]);
      
  const rerunTaskJob = useCallback((taskId, reRunFailed = false, reReunTask = false) => {
    return () => {
      if (!hasFilePermissionFeature) {
        setTaskId(taskId);
        const selectedJob = {
          ...currentJob,
          details : getTasksDevicesCounts(currentJob?.tasks)
        };
        dispatch(displayRerunJobSearchEngineDialog(
          selectedJob,
          reRunFailed,
          reReunTask ? !reReunTask : !reRunFailed,
          reReunTask
        ));
      }
    };
  }, [currentJob, hasFilePermissionFeature]);
      
  const onCancel = useCallback(() => {
    dispatch(cancelJobs(currentJob));
  }, [currentJob]);
    
  const {
    hasDeletePermission,
    hasUpdatePermission
  } = jobsPermissions || {};
    
  const onRerunCancel = useCallback(function() {
    dispatch(displayRerunJobSearchEngineDialog(rerunSelected, false, false));
  }, [rerunSelected]);

  const rerunFailedAllowed = useCallback(() => {
    return isRerunFailedAllowed(currentJob, currentJob?.progressStatus, true);
  }, [currentJob]);
    
  const onDisable = useCallback(() => {
    if (hasDeletePermission) {
      return isJobCancelDisabled(currentJob?.progressStatus);
    }
    return true;
  }, [currentJob]);

  const getActions = function() {
    return (<div className={classes.icons}>
      <IonTooltip caption={translate('jobsearchengine:grid.menu.rerunFailedJobTitle')}>
        <IconButton onClick={rerunTaskJob(0, true)}
          isDisableBackgroundColor={false}
          disabled={!rerunFailedAllowed() || hasFilePermissionFeature || !hasUpdatePermission}
          icon={<RetryIcon size={IconSize.medium}
            opacity={0.5}
            disable={!rerunFailedAllowed() || hasFilePermissionFeature || !hasUpdatePermission}/>} />
      </IonTooltip>
      <IonTooltip caption={translate('jobsearchengine:grid.menu.cancel')}>
        <IconButton onClick={onCancel} disabled={onDisable()} isDisableBackgroundColor={false}
          icon={<CancelIcon size={IconSize.medium}
            disable={onDisable()}
            opacity={0.5} />} />
      </IonTooltip>
      <RefreshGridButton onRefresh={onRefreshPage as (params?: boolean) => void} />
      <CSVExportButton onExport={onExport}
        disableExport={enableEmailExport}
        exportLimit={enableEmailExport}
        hasNoRowSelection={enableEmailExport}
        enableExport={false} />
    </div>);
  };
  
  return (
    <IonDialog isMaxSize
      isViewMode
      isAdjustZIndex={showViewDevice && isTaskDeviceClicked}
      open={open}
      onClose={onCloseDialog}
      actions={
        <TertiaryButton onClick={onCloseDialog}>
          {translate('common:editors.shared.close')}
        </TertiaryButton>
      }
      title={`${translate('jobsearchengine:jobDetails.jobId')}: ${selectedJobId}`}
      shouldSupportContentHeight={!isLoading}
      shouldUpdateContentFlex={!isLoading}>
      <>
        <JobTab ref={jobTabRef}
          enableApi
          includeActions
          actions={getActions()}
          setTaskId={setTaskId}
          jobsPermissions={jobsPermissions}
          jobId={selectedJobId}
          setCurrentJob={setCurrentJob}
          setIsRerunFailed={setIsRerunFailed}
          setIsJobLoading={setIsLoading}
          setIsTaskDeviceClicked={setIsTaskDeviceClicked}
          Features={Features}
          rerunJob={rerunTaskJob}
          isFacetOpen={false}
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          params={params}
          activeTab={''}/>
      </>
      <DownloadJobRerunPrompt show={rerunShow || rerunFailedShow || rerunFailedTaskShow}
        rerunFailedTaskShow={rerunFailedTaskShow}
        rerunFailedShow={rerunFailedShow}
        onRerunConfirm={onRerunConfirm}
        onRerunCancel={onRerunCancel}
        isRerunFailed={isRerunFailed}
        setIsRerunFailed={setIsRerunFailed}
        downloadjob={rerunSelected}
        result={reRunJobSelected}
        loading={jobRerunLoading}
        error={reRunJobError}
        taskId={taskId} />
      <DownloadJobCancelPrompt show={cancelShow}
        isCancelJob={true}
        onDeleteConfirm={onJobDeleteConfirm}
        onDeleteCancel={onJobDeleteCancel}
        overrideMessage={getJobDeleteMessage}
        downloadjob={cancelSelected}
        result={jobCancelSuccess && successMessage}
        loading={jobCancelLoading}
        error={jobCancelError && failureMessage} />
      <Render.If condition={isEmailExportRequest}>
        <EmailExportRequestDialog exportCount={0}
          show={isEmailExportRequest}
          isIndividualJob
          isJobSearchEngine={enableEmailExport}
          exportContext={ExportContext.jobs}
          format={DownloadFileTypes.XLSX}
          sortOn={jobTabRef?.current?.sortOn}
          searchFilters={jobTabRef?.current?.searchCriteria?.trim()}
          viewSubGroups
          getExportColumnDefinitions={getExportColumnDefinitions}
          accountId={accountId}
          showSnackbar={onComplete}
          onClose={onCloseEmailExportDialog} />
      </Render.If>
      <SnackbarMessage show={showBulkSnackbar}
        message={bulkSnackbarMessage}
        onDismiss={dismissBulkSnackbar} />
    </IonDialog>
  );
};

export default ViewJob;
